import { $t } from "@/i18n";
import { CONTENT_TEMPLATE_TYPE } from "../../../helpers/constants"
import errorDialog from "@/views/ErrorDialog/errorDialog";

export const EMAIL_AUTOMATION_PLACEHOLDER_TYPES = {
    PROJECTS_AND_SUPPLIERS: 'projects_and_suppliers',
    CLIENT_COMMUNICATION: 'client_communication'
}

export default class Model {
    constructor(showPlaceholders, companyId, onDeleteFunc, languages, canEditCompany, loader, titleText, type, channel, showErrorsFunc) {
        this.templateName = ''
        this.isPublic = false
        this.errors = {}
        this.showPlaceholders = showPlaceholders
        this.companyId = companyId
        this.onDeleteFunc = onDeleteFunc
        this.languages = languages
        this.selectedLocaleCode = null
        this.templateId = null
        this.sentData = null
        this.titleText = titleText
        this.rows = []
        this.type = type
        this.channel = channel
        this.canEditCompany = canEditCompany
        this.creatorId = null
        this.loader = loader
    }

    showOptions() {
        return this.type === CONTENT_TEMPLATE_TYPE.EMAIL_AUTOMATION
    }

    getTemplateName() {
        return this.templateName
    }

    setTemplateName(value) {
        this.templateName = value
        this.errors.template_name = null
    }

    getIsPublic() {
        return this.isPublic
    }

    setIsPublic(value) {
        this.isPublic = value
    }

    canEditIsPublic() {
        return this.isNewTemplate() && this.canEditCompany
    }

    isPublicDisabled() {
        return Boolean(!this.canEditCompany)
    }

    isTemplateEditable() {
        return (
            (this.canEditCompany && this.isPublic) ||
            !this.isPublic
        )
    }

    canDeleteTemplate() {
        return !this.isNewTemplate() && this.isTemplateEditable() && this.type === CONTENT_TEMPLATE_TYPE.EMAIL_AUTOMATION
    }

    canEditTemplate() {
        return (this.getIsPublic() && this.canEditCompany) || !this.getIsPublic()
    }

    getErrors() {
        return this.errors
    }

    getShowPlaceholders() {
        return this.showPlaceholders
    }

    getCompanyId() {
        return this.companyId
    }

    getActionItems() {
        let items = []

        if (this.canDeleteTemplate()) {
            items.push({
                text: $t("Delete template"),
                action: () => this.onDeleteFunc()
            })
        }

        return items
    }

    getSentRowErrorKey (type, localeCode) {
        if (!this.sentData) return
        let sentRowIndex = this.sentData.rows.findIndex(x => x.locale_code === localeCode)
        if (sentRowIndex < 0) return
        let sentRow = this.sentData.rows[sentRowIndex]

        if (sentRow.locale_code === localeCode) {
            return `${sentRowIndex}.${type}`
        }
    }

    getSentRowError (type, localeCode) {
        const errorKey = this.getSentRowErrorKey(type, localeCode)
        if (errorKey) {
            return this.errors[errorKey]
        }
    }

    isTabError (localeCode) {
        return this.getSentRowError('content', localeCode) || this.getSentRowError('subject', localeCode)
    }

    getActiveTabIndex() {
        return this.languages.findIndex(language => language.code === this.selectedLocaleCode)
    }

    getContentError() {
        return this.getSentRowError('content', this.selectedLocaleCode)
    }

    setContentError() {
        const errorKey = this.getSentRowErrorKey('content', this.selectedLocaleCode)
        if (errorKey) {
            this.errors[errorKey] = null
        }
    }

    getSubjectError() {
        return this.getSentRowError('subject', this.selectedLocaleCode)
    }

    setSubjectError() {
        const errorKey = this.getSentRowErrorKey('subject', this.selectedLocaleCode)
        if (errorKey) {
            this.errors[errorKey] = null
        }
    }

    tabHasContent = (localeCode) => {
        const row = this.rows.find(x => x.locale_code === localeCode)
        if (!row) {
            return false
        }

        return row.subject || row.content
    }

    setSubject(value) {
        this.getSelectedRow().subject = value;
        this.setSubjectError()
    }

    getSubject() {
        return this.getSelectedRow().subject
    }

    setContent(value) {
        this.getSelectedRow().content = value;
        this.setContentError()
    }

    getContent() {
        return this.getSelectedRow().content;
    }

    getSelectedLocaleCode() {
        return this.selectedLocaleCode
    }

    setSelectedLocaleCode(value) {
        this.selectedLocaleCode = value
    }

    getTitleText() {
        return this.titleText
    }

    getSelectedRow() {
        return this.rows.find(x => x.locale_code === this.selectedLocaleCode)
    }

    addRow(row) {
        this.rows.push(row)
    }

    isNewTemplate() {
        return !this.templateId
    }

    getMappedErrorObject(error) {
        let { field } = error;
        return { [field]: error.error_message }
    }

    setError(error) {
        const errorObj = this.getMappedErrorObject(error)
        if (!this.errors[error.index]) {
            this.errors[error.index] = {}
        }

        this.errors = Object.assign({}, this.errors, errorObj);
    }

    findGlobalErrors(errors) {
        return Array.from(errors)
            .filter(error => !error.field)
            .map(error => error.error_message)
    }

    setGlobalErrors (errors) {
        if (this.findGlobalErrors(errors).length > 0) {
            errorDialog.showError(errors[0].error_message)
        }
    }

    clearErrors() {
        this.errors = {}
    }

    getSaveData () {
        const rowsWithContent = this.rows.filter(x => x.subject !== '' || x.content !== '')

        return {
            account_id: this.companyId,
            is_public: this.isPublic,
            template_name: this.templateName,
            type: this.type,
            channel: this.channel,
            rows: rowsWithContent,
            id: this.templateId,
            creator_id: this.creatorId,
            selected_local_code: this.selectedLocaleCode,
        }
    }

    isLoading () {
        return this.loader.isLoading
    }

    setCreatorId(value) {
        this.creatorId = value
    }

    setRows(value) {
        this.rows = value
    }

    setTemplateId(value) {
        this.templateId = value
    }

    setSentData(value) {
        this.sentData = value
    }
}